import { useEffect } from 'react';
import {
  useAppSelector,
  getEditPaymentDetails,
  getSelectedItems,
  updateSelectedItemsMap,
  PaymentDetailsMap,
  clearSelectedItemsMap,
  clearEditPaymentDetails,
  clearSelectedItemIds,
} from '@zonofi/zono-money-store';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { dispatch } from '@zonofi/zono-money-store';
import { PaymentFieldKeys, PaymentModeType } from '../../../constants';
import { getCurrencySign } from '@zonofi/common/functions/format-amount';
import { useCustomNavigate } from '../../../helper';

export const useInitializePaymentData = ({
  setPaymentDetails,
  setPaymentState,
  setUploadedMedia,
}: {
  setPaymentDetails: React.Dispatch<React.SetStateAction<PaymentDetailsMap>>;
  setPaymentState: React.Dispatch<
    React.SetStateAction<{
      paymentMode: PaymentModeType | null;
      paymentType: string;
    }>
  >;
  setUploadedMedia: React.Dispatch<React.SetStateAction<any[]>>;
}) => {
  const { onBack } = useCustomNavigate();

  const paymentData = useAppSelector(getEditPaymentDetails);
  const selectedInvoiceItems = useAppSelector(getSelectedItems);

  useEffect(() => {
    if (paymentData?.paymentDetails?.length > 0) {
      const initialData: PaymentDetailsMap = {};

      paymentData.paymentDetails.forEach((field) => {
        let value = field.value ?? '';

        switch (field.key) {
          case PaymentFieldKeys.PAYMENT_DATE:
            if (typeof value === 'string' && value !== 'Invalid Date') {
              initialData[field.key] = value;
            }
            break;
          case PaymentFieldKeys.PAYMENT_TIME:
            if (typeof value === 'string') {
              const parsed = dayjs(value, ['h:mm A', 'H:mm']);
              value = parsed.isValid() ? parsed.format('HH:mm') : value;
            }
            break;
          case PaymentFieldKeys.AMOUNT_PAID:
            if (typeof value === 'string') {
              const symbol = getCurrencySign() || '';
              const escaped = symbol.replace(/[-\\/\\^$*+?.()|[\]{}]/g, '\\$&');
              value = value.replace(new RegExp(`^${escaped}\\s?`), '');
            }
            break;
          default:
            break;
        }
        initialData[field.key] = value;
      });
      if (paymentData?.tempId) {
        initialData.tempId = paymentData.tempId;
      }
      setPaymentDetails(initialData);

      const modeField = paymentData.paymentDetails.find(
        (field) => field.key === PaymentFieldKeys.PAYMENT_MODE
      );
      if (modeField?.value) {
        setPaymentState((prev) => ({
          ...prev,
          paymentMode: modeField.value as PaymentModeType,
        }));
      }
    }

    if (paymentData?.paymentMedia?.length > 0) {
      setUploadedMedia(paymentData.paymentMedia);
    }
  }, [paymentData?.paymentDetails, paymentData?.paymentMedia]);

  useEffect(() => {
    if (
      paymentData?.paymentInvoice?.length > 0 &&
      isEmpty(selectedInvoiceItems)
    ) {
      paymentData.paymentInvoice.forEach((invoice) => {
        dispatch(updateSelectedItemsMap(invoice));
      });
    }
  }, [paymentData?.paymentInvoice, selectedInvoiceItems]);

  const handleBackState = () => {
    dispatch(clearSelectedItemsMap());
    dispatch(clearSelectedItemIds());
    dispatch(clearEditPaymentDetails());
    onBack();
  };

  useEffect(() => {
    const handleWindowBack = () => {
      handleBackState();
    };

    window.addEventListener('popstate', handleWindowBack);
    return () => {
      window.removeEventListener('popstate', handleWindowBack);
    };
  }, []);

  return { handleBackState };
};
